import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import {
	CustomStringSaveModel,
	UserManagementCustomFieldModel,
	getCustomStrings,
	saveCustomStrings,
	UserManagementCustomStringsSaveModel,
	UserManagementCustomFieldCollectionModel
} from "client/Api/UserManagement/userManagement";
import {
	AlertBar,
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toggle,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { IValueInputAttachable } from "client/Forms/attachable";
import { FormattedTextString } from "client/Misc/commonTypes";
import styles from "./ManageCustomFieldsDialog.module.less";

export type ManageCustomFieldsDialogProps = {
	onClose: () => void;
};

export const ManageCustomFieldsDialog = (props: ManageCustomFieldsDialogProps): ReactElement => {
	const {
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<UserManagementCustomStringsSaveModel>(EMPTY_FORM);
	const [isAlertDismissed, setIsAlertDismissed] = useState<boolean>(false);

	const formHandler: IFormHandler<UserManagementCustomStringsSaveModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	useEffect(
		(): (() => void) => {
			async function fetch(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					const result: UserManagementCustomFieldCollectionModel = await getCustomStrings(signal);
					setFormData(convertToSaveModel(result));
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetch(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validationResult: InputValidationResult = await formHandler
				.validation
				.validate();

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				setIsBusy(false);
				return;
			}

			try {
				await saveCustomStrings(formData, globalCancellation);
				setIsBusy(false);
				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);
				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onClose
		]
	);

	return (
		<Dialog
			trackingId="manage-custom-fields"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_ManageCustomFields")}
				onClose={onClose}
			/>
			<Dialog.Body>
				{!isAlertDismissed && (
					<div className={styles.alertContainer}>
						<AlertBar
							kind="info"
							isInline
							isDismissable
							onDismiss={() => setIsAlertDismissed(true)}
						>
							{localize("_ManageCustomFieldsDialogInfo")}
						</AlertBar>
					</div>
				)}
				<Form onSubmit={submitHandler}>
					<CustomString
						fieldNumber={1}
						attachable={formHandler.for("string1")}
					/>
					<CustomString
						fieldNumber={2}
						attachable={formHandler.for("string2")}
					/>
					<CustomString
						fieldNumber={3}
						attachable={formHandler.for("string3")}
					/>
					<CustomString
						fieldNumber={4}
						attachable={formHandler.for("string4")}
					/>
					<CustomString
						fieldNumber={5}
						attachable={formHandler.for("string5")}
					/>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={submitHandler}
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ManageCustomFieldsDialog.displayName = "ManageCustomFieldsDialog";

type CustomStringProps = {
	fieldNumber: number;
	attachable: IValueInputAttachable<CustomStringSaveModel>;
};

const CustomString = (props: CustomStringProps): ReactElement => {
	const {
		fieldNumber,
		attachable
	} = props;

	const label: FormattedTextString = {
		template: localize("_CustomFieldN"),
		replacements: [
			{
				what: "{{N}}",
				filler: fieldNumber.toString()
			}
		]
	};

	const isEnabled: boolean = attachable
		.for("isEnabled")
		.reducer
		.value;

	return (
		<FormItem
			label={label}
			isRequired
		>
			<div className={styles.inputWithToggle}>
				<div className={styles.inputContainer}>
					<TextInput
						{...attachable.for("displayName").attachToTextInput()}
						placeholder={localize("_EnterDisplayName")}
						isDisabled={!isEnabled}
					/>
					<ValidationError {...attachable.for("displayName").attachToValidationError()} />
				</div>
				<Toggle
					{...attachable.for("isEnabled").attachToCheckbox()}
				/>
			</div>
		</FormItem>
	);
};
CustomString.displayName = "CustomString";

const EMPTY_FIELD: CustomStringSaveModel = {
	displayName: "",
	isEnabled: false
};

const EMPTY_FORM: UserManagementCustomStringsSaveModel = {
	string1: EMPTY_FIELD,
	string2: EMPTY_FIELD,
	string3: EMPTY_FIELD,
	string4: EMPTY_FIELD,
	string5: EMPTY_FIELD
};


function convertToSaveModel(
	model: UserManagementCustomFieldCollectionModel
): UserManagementCustomStringsSaveModel {
	return {
		string1: convertCustomStringToSaveModel(model.string1),
		string2: convertCustomStringToSaveModel(model.string2),
		string3: convertCustomStringToSaveModel(model.string3),
		string4: convertCustomStringToSaveModel(model.string4),
		string5: convertCustomStringToSaveModel(model.string5),
	};
}

function convertCustomStringToSaveModel(
	field: UserManagementCustomFieldModel
): CustomStringSaveModel {
	return {
		displayName: field.displayName ?? "",
		isEnabled: field.isEnabled
	};
}

const validationConfiguration: IValidationConfiguration<UserManagementCustomStringsSaveModel> = createValidationConfiguration<UserManagementCustomStringsSaveModel>();

validationConfiguration
	.forObject("string1")
	.addValidators(customStringValidator());
validationConfiguration
	.forObject("string2")
	.addValidators(customStringValidator());
validationConfiguration
	.forObject("string3")
	.addValidators(customStringValidator());
validationConfiguration
	.forObject("string4")
	.addValidators(customStringValidator());
validationConfiguration
	.forObject("string5")
	.addValidators(customStringValidator());

function customStringValidator(): IValidationConfiguration<CustomStringSaveModel> {
	const result: IValidationConfiguration<CustomStringSaveModel> = createValidationConfiguration<CustomStringSaveModel>();

	result
		.when(x => x.isEnabled)
		.forScalar("displayName")
		.add(
			isNotNullNorUndefined(),
			isNotEmpty(),
			doesNotExceedLength(255)
		);

	return result;
}